import { ArrowUpRight } from "lucide-react";
import Reveal, { SectionHeading } from "@/components/Reveal";
import { scrollToSection } from "@/components/SmoothScroll";

function ServiceCard({ testid, number, title, copy, features, image, className = "", height = "h-[480px] md:h-[560px]" }) {
  return (
    <Reveal className={className}>
      <article
        data-testid={testid}
        className={`group relative overflow-hidden border border-white/10 bg-surface hover:border-gold/40 transition-colors duration-500 ${height}`}
      >
        <img
          src={image}
          alt={title}
          loading="lazy"
          className="absolute inset-0 w-full h-full object-cover opacity-50 transition-all duration-[900ms] ease-out group-hover:scale-105 group-hover:opacity-60"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/45 to-black/10" />
        <span className="absolute top-6 right-7 font-display italic text-2xl text-gold/80">{number}</span>
        <div className="absolute inset-x-0 bottom-0 p-8 md:p-10">
          <h3 className="font-display text-3xl md:text-5xl tracking-tight">{title}</h3>
          <p className="mt-4 text-sm md:text-base text-cream/60 font-light leading-relaxed max-w-md">{copy}</p>
          <ul className="mt-5 space-y-2">
            {features.map((f) => (
              <li key={f} className="flex items-center gap-3 text-[13px] text-cream/70">
                <span className="w-1.5 h-1.5 rotate-45 bg-gold shrink-0" aria-hidden="true" />
                {f}
              </li>
            ))}
          </ul>
          <button
            data-testid={`${testid}-cta`}
            onClick={() => scrollToSection("contact")}
            className="mt-7 inline-flex items-center gap-2 text-[12px] uppercase tracking-[0.25em] text-gold hover:text-gold-soft transition-colors duration-300"
          >
            Get a quote
            <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
          </button>
        </div>
      </article>
    </Reveal>
  );
}

export default function Services() {
  return (
    <section id="services" data-testid="services-section" className="py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14 md:mb-20">
          <SectionHeading
            index="01"
            eyebrow="Services"
            title={<>Everything your business needs<br className="hidden md:block" /> to <span className="italic text-gold">look the part.</span></>}
          />
          <Reveal delay={0.2}>
            <p className="max-w-sm text-sm text-cream/50 font-light leading-relaxed">
              One studio for your whole image — the website that wins the click
              and the print that wins the room.
            </p>
          </Reveal>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          <ServiceCard
            testid="service-card-websites"
            number="01"
            title="Websites"
            copy="Fast, beautiful, built to convert. From a sharp one-pager to a full online home — designed around your customers and launched in weeks, not months."
            features={["Bespoke design, never templates", "Speed & search-engine ready", "Words and imagery sorted for you"]}
            image="https://images.pexels.com/photos/8408538/pexels-photo-8408538.jpeg?auto=compress&cs=tinysrgb&w=1200"
            className="md:col-span-8"
          />
          <ServiceCard
            testid="service-card-business-cards"
            number="02"
            title="Business Cards"
            copy="The handshake before the handshake. Cards with real weight, texture and finish — the kind people keep."
            features={["Premium stocks & finishes", "Foil, emboss & spot UV", "Designed from scratch"]}
            image="/sc-cards.jpg"
            className="md:col-span-4"
          />
          <ServiceCard
            testid="service-card-leaflets"
            number="03"
            title="Leaflets & Flyers"
            copy="Print that pulls its weight. Leaflets, flyers and menus that get picked up — and get kept."
            features={["Retail & menu formats", "Luxury or high-volume runs", "Artwork you own forever"]}
            image="/sc-leaflet-build.jpg"
            className="md:col-span-5"
            height="h-[440px] md:h-[480px]"
          />

          <Reveal className="md:col-span-7">
            <article
              data-testid="service-card-bespoke"
              className="group relative h-[440px] md:h-[480px] border border-gold/25 bg-[radial-gradient(ellipse_at_70%_20%,rgba(212,175,55,0.14),transparent_60%)] hover:border-gold/50 transition-colors duration-500 flex flex-col justify-between p-8 md:p-12"
            >
              <span className="font-display italic text-2xl text-gold/80 self-end">04</span>
              <div>
                <h3 className="font-display text-3xl md:text-5xl tracking-tight">
                  Something else <span className="italic text-gold">in mind?</span>
                </h3>
                <p className="mt-5 text-sm md:text-base text-cream/60 font-light leading-relaxed max-w-md">
                  Logos, menus, posters, signage, social kits — if it prints or
                  renders on a screen, we make it. One conversation, one quote.
                </p>
                <button
                  data-testid="service-card-bespoke-cta"
                  onClick={() => scrollToSection("contact")}
                  className="btn-shine mt-8 inline-flex items-center gap-2 bg-gold text-ink px-8 py-4 text-[12px] uppercase tracking-[0.25em] font-medium hover:bg-gold-soft transition-colors duration-300"
                >
                  Ask about a project
                  <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
                </button>
              </div>
            </article>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
